/**
 * Input Activity Tracker Service
 *
 * Tracks when users are actively interacting with input fields to prevent
 * unwanted redirects during token expiration scenarios.
 */

class InputActivityTracker {
  private readonly ACTIVITY_TIMEOUT = 2000; // 2 seconds of inactivity before considering user "not active"
  private readonly CHECK_INTERVAL = 500; // Check every 500ms

  private isActive = false;
  private lastActivityTime = 0;
  private checkInterval: NodeJS.Timeout | null = null;
  private activeInputs = new Set<HTMLElement>();

  constructor() {
    this.startTracking();
  }

  /**
   * Start tracking input activity
   */
  private startTracking(): void {
    // Track focus events on input elements
    document.addEventListener('focusin', this.handleFocusIn.bind(this), true);
    document.addEventListener('focusout', this.handleFocusOut.bind(this), true);

    // Track input events (typing, pasting, etc.)
    document.addEventListener('input', this.handleInput.bind(this), true);
    document.addEventListener('keydown', this.handleKeyDown.bind(this), true);

    // Track mouse activity on input elements
    document.addEventListener(
      'mousedown',
      this.handleMouseDown.bind(this),
      true
    );

    // Periodically check if user is still active
    this.checkInterval = setInterval(() => {
      this.updateActivityStatus();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Handle focus in on input elements
   */
  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    if (this.isInputElement(target)) {
      this.activeInputs.add(target);
      this.markActive();
    }
  }

  /**
   * Handle focus out on input elements
   */
  private handleFocusOut(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    if (this.isInputElement(target)) {
      // Don't immediately remove - wait for timeout
      // This prevents flickering when switching between inputs
      setTimeout(() => {
        this.activeInputs.delete(target);
        if (this.activeInputs.size === 0) {
          this.lastActivityTime = Date.now();
        }
      }, 100);
    }
  }

  /**
   * Handle input events (typing, pasting, etc.)
   */
  private handleInput(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.isInputElement(target)) {
      this.markActive();
    }
  }

  /**
   * Handle keydown events
   */
  private handleKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (this.isInputElement(target)) {
      // Don't mark as active for navigation keys when not in input
      const isNavigationKey = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        'Escape',
      ].includes(event.key);
      if (
        !isNavigationKey ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA'
      ) {
        this.markActive();
      }
    }
  }

  /**
   * Handle mousedown events
   */
  private handleMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.isInputElement(target)) {
      this.markActive();
    }
  }

  /**
   * Check if element is an input element
   */
  private isInputElement(element: HTMLElement): boolean {
    if (!element) return false;

    const tagName = element.tagName.toLowerCase();
    const isInput =
      tagName === 'input' || tagName === 'textarea' || tagName === 'select';
    const isContentEditable = element.contentEditable === 'true';
    const hasInputRole =
      element.getAttribute('role') === 'textbox' ||
      element.getAttribute('role') === 'combobox' ||
      element.getAttribute('role') === 'searchbox';

    return isInput || isContentEditable || hasInputRole;
  }

  /**
   * Mark user as actively interacting
   */
  private markActive(): void {
    this.isActive = true;
    this.lastActivityTime = Date.now();
  }

  /**
   * Update activity status based on last activity time
   */
  private updateActivityStatus(): void {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime;

    if (this.isActive && timeSinceLastActivity > this.ACTIVITY_TIMEOUT) {
      // Check if any input is still focused
      const hasFocusedInput =
        this.activeInputs.size > 0 ||
        (document.activeElement &&
          this.isInputElement(document.activeElement as HTMLElement));

      if (!hasFocusedInput) {
        this.isActive = false;
      }
    }
  }

  /**
   * Check if user is currently actively interacting with inputs
   */
  isUserActivelyInputting(): boolean {
    // Update status before checking
    this.updateActivityStatus();

    // Also check if any input is currently focused
    const hasFocusedInput =
      document.activeElement &&
      this.isInputElement(document.activeElement as HTMLElement);

    return this.isActive || hasFocusedInput || this.activeInputs.size > 0;
  }

  /**
   * Get time since last activity in milliseconds
   */
  getTimeSinceLastActivity(): number {
    return Date.now() - this.lastActivityTime;
  }

  /**
   * Force mark as inactive (useful for testing or manual control)
   */
  forceInactive(): void {
    this.isActive = false;
    this.activeInputs.clear();
    this.lastActivityTime = Date.now();
  }

  /**
   * Capture all form input values from the page
   * This is called before redirecting to preserve user input
   */
  captureFormData(): Record<string, any> {
    const formData: Record<string, any> = {};

    try {
      // Capture all input elements
      const inputs = Array.from(
        document.querySelectorAll('input, textarea, select')
      );
      inputs.forEach((input, index) => {
        if (
          input instanceof HTMLInputElement ||
          input instanceof HTMLTextAreaElement ||
          input instanceof HTMLSelectElement
        ) {
          const name = input.name || input.id || `input_${index}`;
          const type = input.type;

          // Skip certain input types
          if (type === 'submit' || type === 'button' || type === 'reset') {
            return;
          }

          // Handle different input types
          if (type === 'checkbox' || type === 'radio') {
            if (input instanceof HTMLInputElement && input.checked) {
              formData[name] = input.value || true;
            }
          } else {
            const value = input.value;
            let defaultValue: string | null = null;

            if (
              input instanceof HTMLInputElement ||
              input instanceof HTMLTextAreaElement
            ) {
              defaultValue = input.defaultValue;
            } else if (input instanceof HTMLSelectElement) {
              // For select elements, get the default selected option value
              const defaultOption = input.options[input.selectedIndex];
              defaultValue = defaultOption ? defaultOption.value : '';
            }

            if (value !== '' && value !== defaultValue) {
              formData[name] = value;
            }
          }
        }
      });

      // Capture contenteditable elements
      const contentEditables = document.querySelectorAll(
        '[contenteditable="true"]'
      );
      contentEditables.forEach((el, index) => {
        const name =
          el.getAttribute('name') ||
          el.getAttribute('id') ||
          `contenteditable_${index}`;
        const value = el.textContent || el.innerHTML;
        if (value && value.trim() !== '') {
          formData[name] = value;
        }
      });

      // Capture React Hook Form data if available
      const rhfForms = document.querySelectorAll('form[data-rhf-form]');
      rhfForms.forEach((form) => {
        const formId = form.id || form.getAttribute('data-form-id') || 'form';
        const formDataAttr = form.getAttribute('data-rhf-values');
        if (formDataAttr) {
          try {
            formData[`${formId}_rhf`] = JSON.parse(formDataAttr);
          } catch {
            // Ignore parse errors
          }
        }
      });
    } catch (error) {
      console.error('Error capturing form data:', error);
    }

    return formData;
  }

  /**
   * Check if there are any unsaved form changes on the page
   */
  hasUnsavedFormData(): boolean {
    try {
      // Check for dirty forms
      const dirtyForms = document.querySelectorAll('form[data-dirty="true"]');
      if (dirtyForms.length > 0) {
        return true;
      }

      // Check for React Hook Form dirty state
      const rhfDirty = document.querySelectorAll('[data-rhf-dirty="true"]');
      if (rhfDirty.length > 0) {
        return true;
      }

      // Check for inputs with modified values
      const inputs = Array.from(
        document.querySelectorAll('input, textarea, select')
      );
      for (const input of inputs) {
        if (
          input instanceof HTMLInputElement ||
          input instanceof HTMLTextAreaElement ||
          input instanceof HTMLSelectElement
        ) {
          const type = input.type;
          if (type === 'submit' || type === 'button' || type === 'reset') {
            continue;
          }

          let defaultValue: string | null = null;
          if (
            input instanceof HTMLInputElement ||
            input instanceof HTMLTextAreaElement
          ) {
            defaultValue = input.defaultValue;
          } else if (input instanceof HTMLSelectElement) {
            const defaultOption = input.options[input.selectedIndex];
            defaultValue = defaultOption ? defaultOption.value : '';
          }

          if (input.value !== defaultValue && input.value !== '') {
            return true;
          }
        }
      }

      // Check contenteditable elements
      const contentEditables = Array.from(
        document.querySelectorAll('[contenteditable="true"]')
      );
      for (const el of contentEditables) {
        if (
          el.hasAttribute('data-dirty') &&
          el.getAttribute('data-dirty') === 'true'
        ) {
          return true;
        }
      }
    } catch (error) {
      console.error('Error checking for unsaved form data:', error);
    }

    return false;
  }

  /**
   * Cleanup - stop tracking
   */
  destroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    // Note: We don't remove event listeners as they're on document
    // and removing them might affect other parts of the app
  }
}

// Export singleton instance
export const inputActivityTracker = new InputActivityTracker();
export default inputActivityTracker;
